import api from './api';

/**
 * Shared-link tracking (#256).
 *
 * Builds a shareable URL for a profile or post with a signed
 * `?trkr=` tracking token appended. The token is minted server-side
 * for the authenticated user and is deterministic per (sharer,
 * content), so re-sharing the same content returns the same URL.
 *
 * Anonymous callers fall back to the bare URL — the server isn't
 * issuing tokens without auth, so the share link stays valid but
 * doesn't attribute clicks.
 */

export type ShareContentType = 'profile' | 'post';

interface BuildShareUrlOpts {
  contentType: ShareContentType;
  /** Profile username (without @) or post UUID. */
  contentId: string;
}

const tokenCache = new Map<string, string>();
const inflightTokens = new Map<string, Promise<string | null>>();

// Profiles get any leading @ stripped so callers can pass either form
// without minting duplicate tokens server-side (and without building
// /profile/@@bob URLs). useParams on /profile/:username hands back the
// @-prefixed handle, so the boundary is the right place to normalize.
function normalizeContentId(contentType: ShareContentType, contentId: string): string {
  return contentType === 'profile' ? contentId.replace(/^@+/, '') : contentId;
}

const cacheKey = (type: ShareContentType, id: string) => `${type}|${normalizeContentId(type, id)}`;

function bareUrlFor(contentType: ShareContentType, contentId: string): string {
  const origin = window.location.origin;
  const id = normalizeContentId(contentType, contentId);
  const path = contentType === 'profile' ? `/profile/@${id}` : `/post/${id}`;
  return `${origin}${path}`;
}

/**
 * Synchronous variant. Returns the share URL with the tracking
 * token *if it's already in the in-memory cache*, otherwise returns
 * the bare URL. Designed to be called inline from a click handler
 * so `navigator.clipboard.writeText` runs in the same tick as the
 * user gesture — Safari (and increasingly Chrome) consumes the
 * "transient user activation" the moment you `await`, after which
 * the clipboard write throws.
 *
 * Pair this with `prefetchShareToken` on menu-open / button-hover
 * so the cache is warm by the time the user clicks Share.
 */
export function getCachedShareUrl({ contentType, contentId }: BuildShareUrlOpts): string {
  const bareUrl = bareUrlFor(contentType, contentId);
  const cached = tokenCache.get(cacheKey(contentType, contentId));
  return cached ? `${bareUrl}?trkr=${cached}` : bareUrl;
}

/**
 * Kick off the network call to mint a share token + warm the cache.
 * Idempotent: concurrent calls share the same in-flight promise so
 * we don't fire duplicate requests on rapid menu opens. Always
 * resolves (never throws) so a failed mint never blocks share UI.
 */
export function prefetchShareToken({ contentType, contentId }: BuildShareUrlOpts): Promise<string | null> {
  const normalizedId = normalizeContentId(contentType, contentId);
  const key = cacheKey(contentType, normalizedId);
  const cached = tokenCache.get(key);
  if (cached) return Promise.resolve(cached);
  const inflight = inflightTokens.get(key);
  if (inflight) return inflight;

  // Send the normalized id to the server so the token signature keys
  // off the bare handle, not whatever variant the caller had on hand.
  const promise = api
    .post('/share/token', { contentType, contentId: normalizedId })
    .then((res) => {
      const trkr: string | undefined = res.data?.trkr;
      if (trkr) tokenCache.set(key, trkr);
      return trkr ?? null;
    })
    .catch(() => null)
    .finally(() => {
      inflightTokens.delete(key);
    });

  inflightTokens.set(key, promise);
  return promise;
}

/**
 * Async variant. Mints + caches if needed, then returns the URL
 * with token attached (or bare URL on failure). Only safe to call
 * outside a click-then-clipboard flow — use `getCachedShareUrl`
 * inside click handlers.
 */
export async function buildShareUrl({ contentType, contentId }: BuildShareUrlOpts): Promise<string> {
  await prefetchShareToken({ contentType, contentId });
  return getCachedShareUrl({ contentType, contentId });
}

/**
 * Read a trkr token from the current URL (if any) and post it to
 * the click-record endpoint. Returns true when a token was found
 * and a request was fired. Caller should strip the param from the
 * URL after this resolves so repeat fires don't happen.
 */
export async function recordShareClickIfPresent(): Promise<boolean> {
  const params = new URLSearchParams(window.location.search);
  const trkr = params.get('trkr');
  if (!trkr) return false;
  try {
    await api.post('/share/click', { trkr });
  } catch {
    // Best-effort. Bad signatures already 204 silently on the server
    // — a network failure here just means the click goes uncounted.
  }
  return true;
}

/** Remove trkr from the URL without a navigation. */
export function stripTrkrFromUrl(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('trkr')) return;
  url.searchParams.delete('trkr');
  window.history.replaceState({}, '', url.toString());
}

/**
 * Copy `text` to the clipboard. Tries `navigator.clipboard.writeText`
 * first; falls back to the legacy `document.execCommand('copy')` via
 * a hidden textarea for environments where the modern API isn't
 * usable — non-secure contexts (LAN IP over HTTP), iframes with a
 * restrictive permissions-policy, browsers that throw synchronously
 * when transient activation has expired, etc.
 *
 * Resolves on success. Rejects with the first encountered error
 * when both paths fail. Logs both paths so failures don't disappear
 * silently — the original symptom of #256 was "click does nothing"
 * because the modern path threw synchronously and the .then(...)
 * chain never ran.
 */
export async function copyToClipboard(text: string): Promise<void> {
  // Primary path: modern Clipboard API. The synchronous part is
  // wrapped in try so a TypeError ("navigator.clipboard is
  // undefined" outside secure contexts) doesn't escape the function.
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch (err) {
    console.warn('[share] navigator.clipboard.writeText failed; falling back to execCommand', err);
  }

  // Fallback path: hidden textarea + execCommand('copy'). Works
  // without secure context, requires the element to be focused +
  // selected before the copy command runs.
  return new Promise((resolve, reject) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '0';
      ta.style.left = '0';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) return resolve();
      reject(new Error('execCommand("copy") returned false'));
    } catch (err) {
      console.error('[share] textarea fallback failed', err);
      reject(err);
    }
  });
}
